import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useStytchMember } from '@stytch/react/b2b';
import { ConnectPage } from './connect/ConnectPage';
import { CreateTeamPage, NEW_TEAM_PATH } from './organization/CreateTeamPage';
import { JoinPage } from './join/JoinPage';
import { PendingJoinGate } from './join/PendingJoinGate';
import { AuthenticationCallback, LoginScreen } from './routes/AuthRoutes';
import { CollabEditorHarnessRoute } from './routes/CollabEditorHarnessRoute';
import { CollabDocsHarnessRoute } from './routes/CollabDocsHarnessRoute';
import { WorkspaceRoutes } from './routes/WorkspaceRoutes';
import { OpenInAppPage, TeamViewLinkRoute } from './links/OpenInAppPage';
import { isEmbedded } from './embedded/embedMarker';
import {
  AuthenticateNativeRoute,
  EmbeddedPersonalLink,
  EmbeddedRouteReporter,
  NativeSessionGate,
} from './embedded/NativeSignIn';

/*
 * GitHub-native wikis load lazily and only when the build switch is on. The
 * comparison is inline on `import.meta.env` so
 * the build folds it to `false` and drops the chunk, `githubSession` included;
 * `scripts/check-production-output.mjs` asserts the bundle carries none of it.
 */
const GitHubWikiRoutes = import.meta.env.VITE_WIKI_GITHUB_NATIVE === 'on'
  ? lazy(() => import('./routes/GitHubWikiRoutes').then((module) => ({ default: module.GitHubWikiRoutes })))
  : null;
const GitHubOAuthCallback = import.meta.env.VITE_WIKI_GITHUB_NATIVE === 'on'
  ? lazy(() => import('./routes/GitHubSignIn').then((module) => ({ default: module.GitHubOAuthCallback })))
  : null;

/* A public knowledge graph: read-only, no sign-in, its own chunk. */
const PublicWikiRoutes = lazy(() => import('./publicWiki/PublicWikiRoutes').then((module) => ({ default: module.PublicWikiRoutes })));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<main className="loading-screen">Loading…</main>}>{children}</Suspense>;
}

function AuthenticatedConsole() {
  const { member, isInitialized } = useStytchMember();
  if (!isInitialized) return <main className="loading-screen">Preparing secure sign-in…</main>;
  // Signed out, the sign-in screen remembers this URL and returns to it.
  // Inside the phone app there is no login page: the app mints the session.
  // An invite link also survives sign-in finishing in another tab (PendingJoinGate).
  if (!member) {
    return <PendingJoinGate signedIn={false}>{isEmbedded() ? <NativeSessionGate /> : <LoginScreen />}</PendingJoinGate>;
  }
  return (
    <PendingJoinGate signedIn>
    <Routes>
      {/* Outside the workspace: none of these needs an org, and a user with no team reaches them. */}
      <Route path="/connect" element={<ConnectPage mode="authorize" />} />
      <Route path="/connect/device" element={<ConnectPage mode="device" />} />
      <Route path={NEW_TEAM_PATH} element={<CreateTeamPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      {/* A view placed in a page: a type's view shows as the type, anything else is offered to the app. */}
      <Route path="/org/:orgSlug/project/:projectId/view/*" element={<TeamViewLinkRoute />} />
      <Route path="*" element={<WorkspaceRoutes />} />
    </Routes>
    </PendingJoinGate>
  );
}

export function App() {
  return (
    <>
    <EmbeddedRouteReporter />
    <Routes>
      {import.meta.env.DEV && <Route path="/__collab-editor-harness" element={<CollabEditorHarnessRoute />} />}
      {import.meta.env.DEV && <Route path="/__collab-docs-harness" element={<CollabDocsHarnessRoute />} />}
      <Route path="/authenticate" element={<AuthenticationCallback />} />
      {/* The phone app's sign-in: refuses to run without the injected embed marker. */}
      <Route path="/authenticate/native" element={<AuthenticateNativeRoute />} />
      {/* GitHub-native wikis gate on the GitHub session, not the Stytch one. */}
      {GitHubOAuthCallback && <Route path="/oauth/callback" element={<Lazy><GitHubOAuthCallback /></Lazy>} />}
      {GitHubWikiRoutes && <Route path="/wiki/*" element={<Lazy><GitHubWikiRoutes /></Lazy>} />}
      {/* Public wikis need no session of any kind; the server serves only the public projection. */}
      <Route path="/public/wiki/*" element={<Lazy><PublicWikiRoutes /></Lazy>} />
      {/* Page links to things only their author's desktop holds; handed to the app, no sign-in needed. */}
      <Route path="/app/*" element={isEmbedded() ? <EmbeddedPersonalLink /> : <OpenInAppPage />} />
      <Route path="*" element={<AuthenticatedConsole />} />
    </Routes>
    </>
  );
}
